import React from 'react';
import {OrderFormProps } from './types';
import { ArcAlert, ArcButton, ArcOutlinedInput, ArcStack, ArcText } from '@src/core/ui';
import { useNavigate } from 'react-router-dom';
import { Controller } from 'react-hook-form';
import { REQUIRED_VALIDATION_TEXT } from '@src/core/config';
import { useDispatch, useSelector } from 'react-redux';
import { setMapCenter } from '@src/core/controllers';
import { ApiErrorResponse, LocationEntity } from '@src/core/entities';
import { GoogleSearchInput } from '@src/core/components';
import { routesConfig } from '@src/core/routes';
import { selectReadOnlyTakeOff, setReadOnlyTakeOff, setTakeOffLocation, setDestinationLocation, selectTakeOffLocation, selectDestinationLocation } from '@src/features/order/pages/upsert-order-page/redux';
import { useCheckAndPlanMutation } from '@src/core/features';

const OrderForm: React.FC<OrderFormProps> = (props) => {
  const { form, isSubmitting, isCanceling, onSubmit  } = props;

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const readOnly = useSelector(selectReadOnlyTakeOff);
  const takeOffLocation = useSelector(selectTakeOffLocation);
  const destinationLocation = useSelector(selectDestinationLocation);

  const [checkAndPlan, { error }] = useCheckAndPlanMutation();

  const onChangeTakeOff = (location: LocationEntity) => {
    const { latitude, longitude } = location;
    form.setValue('takeOffLocation', location);
    dispatch(setTakeOffLocation(location));
    dispatch(setMapCenter({ lat: Number(latitude), lng: Number(longitude) }));
  };

  const onChangeDestination = (location:  LocationEntity) => {
    const { latitude, longitude } = location;
    form.setValue('destinationLocation', location);
    dispatch(setDestinationLocation(location));
    dispatch(setMapCenter({ lat: Number(latitude), lng: Number(longitude) }));
  };

  const onChangeReadOnly = (value: boolean) => {
    dispatch(setReadOnlyTakeOff(value));
  };

  React.useEffect(() => {
    if (!takeOffLocation || !destinationLocation) return;
  
    const path1 = { 
      latitude: Number(takeOffLocation.latitude), 
      longitude: Number(takeOffLocation.longitude),
    };
  
    const path2 = {
      latitude: Number(destinationLocation.latitude), 
      longitude: Number(destinationLocation.longitude),
    };
  
    checkAndPlan({
      isSilent: true,
      path: [path1, path2],  
    });
      
  }, [takeOffLocation, destinationLocation]);

  const errors = form.formState.errors;
  const warningError = error as ApiErrorResponse;

  return (
    <ArcStack height='100%' component='form' onSubmit={form.handleSubmit(onSubmit)}>
      <ArcStack height='100%' justifyContent='space-between'>
        <ArcStack spacing={4}>
          <ArcStack spacing={2}>
            <ArcText.h5> TAKEOFF </ArcText.h5>
            <Controller 
              control={form.control} 
              name='takeOffLocation.address'
              rules={{ required: REQUIRED_VALIDATION_TEXT }}
              render={({ field: { value } }) => (
                <GoogleSearchInput
                  initValue={value} 
                  label='Takeoff Location'
                  readOnly={readOnly}
                  error={Boolean(errors.takeOffLocation?.address)} 
                  helperText={errors.takeOffLocation?.address?.message}
                  onSelect={onChangeTakeOff}
                  endAdornment={
                    <ArcStack>
                      {readOnly 
                        ? (
                          <ArcText.h6 
                            color='brand'
                            sx={{ cursor: 'pointer' }}
                            onClick={() => onChangeReadOnly(false)}> 
                            Edit 
                          </ArcText.h6> 
                        ) : (
                          <ArcText.h6 
                            color='brand' 
                            sx={{ cursor: 'pointer' }}
                            onClick={() => onChangeReadOnly(true)}>
                            Done 
                          </ArcText.h6>
                        )}
                    </ArcStack>
                  } />
              )}
            />
          </ArcStack>

          <ArcStack spacing={2}>
            <ArcText.h5> DESTINATION </ArcText.h5>
            <Controller 
              control={form.control} 
              name='destinationLocation.address' 
              rules={{ required: REQUIRED_VALIDATION_TEXT }}
              render={({ field: { value } }) => (
                <GoogleSearchInput 
                  initValue={value} 
                  label='Delivery Location'
                  error={Boolean(errors.destinationLocation?.address)} 
                  helperText={errors.destinationLocation?.address?.message}
                  onSelect={onChangeDestination} />
              )}
            />
            <ArcStack direction='row' spacing={2}>
              <Controller 
                name='destinationLocation.latitude'
                control={form.control} 
                rules={{ required: REQUIRED_VALIDATION_TEXT }} 
                render={({ field }) => (
                  <ArcOutlinedInput 
                    {...field} 
                    label='Latitude' 
                    error={Boolean(errors?.destinationLocation?.latitude)}
                    helperText={errors?.destinationLocation?.latitude?.message} />
                )} 
              />

              <Controller 
                name='destinationLocation.longitude'
                control={form.control} 
                rules={{ required: REQUIRED_VALIDATION_TEXT }} 
                render={({ field }) => (
                  <ArcOutlinedInput 
                    {...field} 
                    label='Longitude'
                    error={Boolean(errors?.destinationLocation?.longitude)}
                    helperText={errors?.destinationLocation?.longitude?.message} />
                )} 
              />

            </ArcStack>
          </ArcStack>

          {Boolean(warningError) && (
            <ArcAlert severity='warning'>
              <ArcText.h6 color='warning.main'> {warningError.data.message} </ArcText.h6>
            </ArcAlert>
          )}

          <ArcStack spacing={2}>
            <ArcText.h5> DETAILS </ArcText.h5>

            <Controller 
              name='packageContent'
              control={form.control} 
              render={({ field }) => (
                <ArcOutlinedInput 
                  {...field} 
                  label='Package Content'  />
              )} 
            />

            <Controller 
              name='clientName'
              control={form.control} 
              render={({ field }) => (
                <ArcOutlinedInput 
                  {...field} 
                  label='Client Name' />
              )} 
            />
          </ArcStack>
        </ArcStack>

        <ArcStack spacing={1}>
          <ArcStack direction='row' spacing={1}>
            <ArcButton.ghost 
              fullWidth 
              onClick={() => navigate(-1)}
              isLoading={isCanceling}> 
              Cancel 
            </ArcButton.ghost>
            <ArcButton.ghost 
              fullWidth 
              onClick={() => navigate(routesConfig.orders)}
              isLoading={isSubmitting}> 
              Exit 
            </ArcButton.ghost>
          </ArcStack>
          <ArcButton.brand 
            fullWidth 
            type='submit' 
            isLoading={isSubmitting}> 
            Next 
          </ArcButton.brand>
        </ArcStack>
      </ArcStack>
    </ArcStack>
  );
};

export default OrderForm;